import { useState, useEffect } from 'react';

// Static import as fallback (compiled into bundle)
import staticBlogPosts from '../data/blogPosts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Custom hook to fetch blog posts dynamically.
 * 
 * Fetches in this priority order:
 * 1. API endpoint (/api/blog/posts) - Database-backed posts
 * 2. JSON file (/data/blogPosts.json) - Generated file
 * 3. Static import (staticBlogPosts) - Compiled into bundle
 * 
 * This allows new blog posts to appear immediately without frontend rebuilds.
 */
const useBlogPosts = () => {
  const [posts, setPosts] = useState(staticBlogPosts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // First try: API endpoint (database-backed posts)
        const apiResponse = await fetch(`${API_URL}/api/blog/posts?per_page=100`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        
        if (apiResponse.ok) {
          const data = await apiResponse.json();
          if (data.posts && data.posts.length > 0) {
            // Transform API response to match expected format
            const formattedPosts = data.posts.map(post => ({
              id: post.id.toString(),
              title: post.title,
              slug: post.slug,
              excerpt: post.excerpt || '',
              metaDescription: post.meta_description || post.excerpt || '',
              keywords: post.keywords || '',
              date: post.date,
              author: post.author || 'Vaulto Team',
              readTime: post.read_time || post.readTime || '5 min read',
              image: post.image || post.featured_image || '/vaulto.png',
              category: post.category,
              content: post.content
            }));
            setPosts(formattedPosts);
            setLoading(false);
            return;
          }
        }
      } catch (apiErr) {
        console.log('API fetch failed, trying JSON file...');
      }

      try {
        // Second try: JSON file
        const jsonResponse = await fetch('/data/blogPosts.json', {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        
        if (jsonResponse.ok) {
          const dynamicPosts = await jsonResponse.json();
          if (dynamicPosts && dynamicPosts.length > 0) {
            setPosts(dynamicPosts);
            setLoading(false);
            return;
          }
        }
      } catch (jsonErr) {
        console.log('JSON fetch failed, using static posts');
        setError(jsonErr);
      }

      // Third try: Already using static posts as default
      setLoading(false);
    };

    fetchPosts();
  }, []);

  return { posts, loading, error };
};

/**
 * Get a single blog post by slug.
 * First tries API, then falls back to local list.
 */
export const useBlogPost = (slug) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { posts } = useBlogPosts();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // Try API first
        const response = await fetch(`${API_URL}/api/blog/posts/${slug}`, {
          cache: 'no-store'
        });
        
        if (response.ok) {
          const data = await response.json();
          setPost({
            id: data.id?.toString() || data.slug,
            title: data.title,
            slug: data.slug,
            excerpt: data.excerpt || '',
            metaDescription: data.meta_description || data.excerpt || '',
            keywords: data.keywords || '',
            date: data.date,
            author: data.author || 'Vaulto Team',
            readTime: data.read_time || '5 min read',
            image: data.featured_image || '/vaulto.png',
            images: data.images,
            category: data.category,
            content: data.content
          });
          setLoading(false);
          return;
        }
      } catch (err) {
        console.log('API fetch failed for single post, using list');
      }

      // Fallback to list
      const foundPost = posts.find(p => p.slug === slug) || null;
      setPost(foundPost);
      setLoading(false);
    };

    if (slug) {
      fetchPost();
    }
  }, [slug, posts]);

  return { post, loading };
};

export default useBlogPosts;
